import moment from "moment-timezone";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { useCancellablePromise } from "src/API/useCancellablePromise";
// Components
import { CardContent, Grid, Theme, Typography, Button, styled } from "@mui/material";
import { SpinnerProgress } from "src/components/SpinnerProgress/SpinnerProgress";
// Form fields
import { DateTimeField } from "src/components/FormikComponents/DateTimeField";
import { SelectField } from "src/components/FormikComponents/SelectField";
import { SwitchField } from "src/components/FormikComponents/SwitchField";
import { TextField } from "src/components/FormikComponents/TextField";
import FlightPairingStatusContextMenu from "./FlightPairingStatusContextMenu";
// Table related imports
import { FilterSearchTable } from "src/components/FilterSearchTable/FilterSearchTable";
import {
  IFlightPairingStatusRow,
  pairingStatusDesc,
  returnMonthDayCount,
  returnPairingStatusInfo,
  returnPairingStatusRows,
} from "./PairingTableData";
// Redux imports
import { useDispatch, useSelector } from "react-redux";
import { setPairingStatusMap } from "src/redux/actions/FlightPairingStatus/FlightPairingStatus.Actions";
import { IRootState } from "src/redux/reducers/CombinedReducers";

// Airport options
import { IAirportOption, IAirportOptionMap } from "src/redux/reducers/StructuresReducer";
import { updateAirportOptions } from "src/utils/airportHelper";

import { IAircraftGroup, IFlightPairingStatus, IFlightType, IPosition } from "@vmosolutions/oc-interfaces";
import { DialogModalWrapper, EventModalTypes } from "src/components/DialogModal/DialogModalWrapper";
import { flightStatusMessages } from "./helpers";

interface IFlightPairingStatusWindowProps {
  id: string;
  path: string[];
  windowProps: any;
}

const crewStatus = (theme: Theme): object => ({
  height: 20,
  textAlign: "center",
  margin: 5,
  color: theme.palette.background.default,
  fontSize: 10,
  border: `1px solid ${theme.palette.background.default}`,
  borderRadius: 3,
  padding: "2px 8px",
  fontWeight: 700,
  textShadow: "1px 1px rgba(255,255,255,0.5)", // alphad drop shadow on text to improve legibility.
  // Flight Pairing Status Colors
  "&.no_pairing": { backgroundColor: theme.custom.pairingStatus.noPairingColor },
  "&.missing_crew": { backgroundColor: theme.custom.pairingStatus.missingCrewColor },
  "&.extra_crew": { backgroundColor: theme.custom.pairingStatus.extraCrewColor },
  "&.extra_pos": { backgroundColor: theme.custom.pairingStatus.extraPosColor },
  "&.missing_ac_info": { backgroundColor: theme.custom.pairingStatus.missingACInfoColor },
  "&.ok": { backgroundColor: theme.custom.pairingStatus.okColor },
});

const OCFlightPairingStatus = styled(CardContent)(({ theme }) => ({
  backgroundColor: theme.custom.timeline.contextListBackground,
  width: "100%",
  "&:last-child": { paddingBottom: 0 },
  "& .flightPairingStatusInfo": {
    padding: 5,
    minWidth: 210,
    marginRight: 10,

    "& .window_title": {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      "& .page_header": {
        margin: "5px 0px",
        "& .subheader": {
          margin: "0px 10px",
          color: theme.custom.forms.labels,
        },
      },
      "& .refresh_btn": {
        margin: 5,
        padding: "2px 8px",
        height: 20,
        fontSize: 10,
      },
    },

    "& .filter_container": {
      alignItems: "center",
      "& .flight_filter": {
        width: "auto",
        flexWrap: "wrap",
        marginLeft: 10,
        paddingLeft: 10,
        borderLeft: `2px solid ${theme.custom.forms.inputBorder}`,
        "& .range_datetimefield": {
          width: "auto",
          "& .input_control label": { width: "auto" },
          "& .input_control .react-datepicker-wrapper": {
            margin: 0,
            "& input": { width: 90 },
          },
        },
        "& .flightnumber_textfield, & .shortcode_textfield": {
          "& .input_control label": { width: "auto" },
          "& .input-base": { width: 50 },
        },
        "& .origin_selectfield, & .destination_selectfield, & .aircraftgroup_selectfield, & .flighttype_selectfield": {
          "& .input_control label": { width: "auto" },
        },
      },
    },

    "& .legend_container": {
      margin: "5px 0px",
      alignItems: "center",
      "& .legend": {
        flexWrap: "wrap",
        marginLeft: 10,
        paddingLeft: 10,
        borderLeft: `2px solid ${theme.custom.forms.inputBorder}`,
        display: "flex",
        flexDirection: "row",
        "& .status": crewStatus(theme),
        "& .refresh_btn": {
          margin: 5,
          padding: "2px 8px",
          height: 20,
          fontSize: 10,
        },
      },
    },
  },
  "& .flightPairingStatusTable": {
    "& .table_info, & .table_controls": { display: "none !important" },
    "& .table_container": {
      "& .table_content .table_row .MuiTableCell-root": {
        verticalAlign: "top",
      },
    },
  },
  "& .loadingSpinner": {
    marginTop: -50,
    marginLeft: -16,
  },
}));

/**
 * Function returned pairing flight status with applied filters.
 * @param rows - table rows
 * @param flightNumber - flight number filter
 * @param origin - origin filter
 * @param destination - destination filter
 */
const filterTableRows = (
  rows: IFlightPairingStatusRow[],
  shortCode: string,
  flightNumber: string,
  origin: IAirportOption,
  destination: IAirportOption,
  aircraftGroup: IAircraftGroup
): IFlightPairingStatusRow[] => {
  const filteredRows = rows.filter((row: IFlightPairingStatusRow) => {
    const filter1 = flightNumber ? row.flightNumber.startsWith(flightNumber) : true;
    const filter2 = origin ? row.origin === origin.ident : true;
    const filter3 = destination ? row.destination === destination.ident : true;

    let includesShortCodes = shortCode ? false : true;
    // If a shortCode filter is passed in, filter out any flights which don't contain a refence to it.
    if (shortCode) {
      for (const key in row) {
        const statusArray: IFlightPairingStatus[] = row[key];
        if (!!statusArray && Array.isArray(statusArray) && statusArray.length > 0) {
          const includesShortCode = statusArray.find((status) => status.pairingShortCodes.includes(shortCode));
          if (!includesShortCode) {
            row[key] = [];
          } else {
            includesShortCodes = true;
          }
        }
      }
    }

    // If an aircraft group is passed in, filter out any flights that aren't assigned to included aircraft.
    let includesGroupAircraft = aircraftGroup ? false : true;
    if (aircraftGroup) {
      for (const key in row) {
        const statusArray: IFlightPairingStatus[] = row[key];
        if (!!statusArray && Array.isArray(statusArray) && statusArray.length > 0) {
          const includesGroupAC = statusArray.find((status) => aircraftGroup.members.includes(status.aircraftId));
          if (!includesGroupAC) {
            row[key] = [];
          } else {
            includesGroupAircraft = true;
          }
        }
      }
    }

    return filter1 && filter2 && filter3 && includesShortCodes && includesGroupAircraft;
  });

  return filteredRows;
};

// Returns epoch values for start/end times of the month of a given Date.
// Note: use utc(false) to ensure values returned are in UTC.
const defaultDateRange = (): number[] => {
  const today = new Date();
  const startEpoch = moment.utc(today).startOf("month")?.valueOf();
  const endEpoch = moment.utc(today).endOf("month")?.valueOf();
  return [startEpoch, endEpoch];
};

/**
 * Display window table for pairing status.
 */
export const FlightPairingStatusWindow = (props: IFlightPairingStatusWindowProps): JSX.Element => {
  const { id, path } = props;

  const dispatch = useDispatch();
  const mosaicPanel = useMemo(() => `${id}-panel`, [id]);
  const { cancellablePromise } = useCancellablePromise();
  const [activeEventModal, setActiveEventModal] = useState<EventModalTypes>(EventModalTypes.None);
  const layout = "column";

  // AC Group Filter Options
  const [aircraftGroupOptions, setAircraftGroupOptions] = useState<IAircraftGroup[]>([]);
  useEffect(() => {
    const getGroups = async (): Promise<void> => {
      const response = await AmplifyAPIWrapper.Get<IAircraftGroup[]>("/aircraftGroup");
      if (Array.isArray(response))
        setAircraftGroupOptions(
          response.sort((a, b) => {
            return a.title.toLowerCase().localeCompare(b.title.toLowerCase());
          })
        );
    };
    getGroups();
  }, []);

  // Set default selection range and start time.
  const [defaultStart, defaultEnd] = defaultDateRange();

  // Selected Data Range (shows all days in current month).
  const [start, setStart] = useState<number>(defaultStart);
  const [end, setEnd] = useState<number>(defaultEnd);

  // Data Filters | shortCode | FlightNumber | Origin | Destionation
  const [shortCode, setShortCode] = useState<string>(null);
  const [flightNumber, setFlightNumber] = useState<string>(null);
  const [origin, setOrigin] = useState<IAirportOption[]>(null);
  const [destination, setDestination] = useState<IAirportOption[]>(null);
  const [aircraftGroup, setAircraftGroup] = useState<IAircraftGroup[]>(null);
  const [flightTypes, setFlightTypes] = useState<IFlightType[]>();
  const [positions, setPositions] = useState<IPosition[]>([]);
  const positionOptions: IPosition[] = useSelector(
    (state: IRootState) => state.structures.customerStructure.positions
  ).sort((a, b) => (b.displayOrder > a.displayOrder ? -1 : 1));
  const airportOptions: IAirportOptionMap = useSelector((state: IRootState) => state.structures.airportOptions);

  // Retrieve Status Map Data stored in redux.
  const flightStatusMap = useSelector((state: IRootState) => state.flightPairingStatus.flightStatusMap);
  const flightTypeList = useSelector((state: IRootState) => state.structures.customerStructure.flightTypes);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [includeSubAircraft, setIncludeSubAircraft] = useState<boolean>(false);

  const getFlightPairingStatus = useCallback(async () => {
    setIsLoading(true);
    const notifications: INotifications = {
      dispatch: dispatch,
      infoMessage: "Retrieving Flight Pairing Status...",
      successMessage: "Flight Pairing Status Successfully Retrieved.",
      errorMessage: "Error: Unable to Retrieve Flight Pairing Status - Decrease Date Range.",
    };
    const statusData: IFlightPairingStatus[] = await AmplifyAPIWrapper.Get(
      `/flights/getFlightPairingStatus/${start}/${end}?incsub=${includeSubAircraft}&origins=${origin?.map(
        (o) => o.ident
      )}&destinations=${destination?.map((d) => d.ident)}&flightNumbers=${flightNumber}&ACGroups=${aircraftGroup?.map(
        (acg) => acg?.title
      )}&shortCodes=${shortCode}&flightTypes=${flightTypes?.map((ft) => ft._id)}&positions=${positions?.map(
        (pos) => pos._id
      )}`,
      notifications,
      cancellablePromise
    );
    // Store data in redux as a hashmap.
    if (Array.isArray(statusData) && statusData.length > 0) {
      dispatch(setPairingStatusMap(statusData));
    }
    setIsLoading(false);
    //! We do not want the cancellablePromise hook to cause a re-render so leave it out of the dependency array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    end,
    includeSubAircraft,
    start,
    origin,
    destination,
    flightNumber,
    aircraftGroup,
    shortCode,
    positions,
  ]);

  // Load Flight Pairing Status data for selected start range (default is a month).
  //useEffect(() => {
  // getFlightPairingStatus();
  //}, [start, end, getFlightPairingStatus, includeSubAircraft]);

  // Generate dynamic page header based on values in start and end times.
  const pageHeader = useMemo(() => {
    const momentStart = moment.utc(start);
    const momentEnd = moment.utc(end); // .add(1, "second");
    const { isFullMonth } = returnMonthDayCount(start, end);
    const subheader = isFullMonth
      ? momentStart.format("MMMM, yyyy")
      : `${momentStart.format("MMM DD, yyyy")} - ${momentEnd.format("MMM DD, yyyy")}`;
    return (
      <Typography className="page_header" variant="h6" color="textPrimary">
        Flight Pairing Status
        <span className="subheader">({subheader})</span>
      </Typography>
    );
  }, [start, end]);

  const filteredTableRows = useMemo(() => {
    const rows: IFlightPairingStatusRow[] = flightStatusMap ? returnPairingStatusRows(flightStatusMap, start, end) : [];
    //the filtering is happening in the backend so no need to do it here
    return rows;
    // const filteredRows = filterTableRows(rows, shortCode, flightNumber, origin, destination, aircraftGroup);
    // return filteredRows;
  }, [flightStatusMap, start, end]);

  /**
   * Create the table data required to display the flight pairing status in the filter search table.
   */
  const tableData = useMemo(() => {
    return [
      {
        id: "flightPairingStatus",
        dataType: "flightPairingStatus",
        label: "flightPairingStatus",
        data: filteredTableRows,
        tableInfo: returnPairingStatusInfo(start, end),
        tableInfoDisplay: pairingStatusDesc,
      },
    ];
  }, [filteredTableRows, start, end]);

  const [scrollTop, setScrollTop] = useState<number>(0);

  useEffect(() => {
    const window = document.getElementById(mosaicPanel);
    if (window) {
      const tableElement = window.getElementsByClassName("table_container")[0];
      // this fix isn't ideal as it's triggered whenever the user is scrolling in the table
      // TODO investigate cause of scroll position being lost in the table when the mosaics are being shuffled around.
      if (tableElement?.scrollTop) {
        tableElement.scrollTop = scrollTop;
      }
    }
  }, [mosaicPanel, path, scrollTop]);

  // DYNAMIC TABLE RESIZE CODE START
  const headerRef = useRef<HTMLDivElement>(null);
  const [tableHeight, setTableHeight] = useState<number>(0);

  const getTableHeight = useCallback((windowId: string): number => {
    const windowHeight = document.getElementById(windowId)?.getBoundingClientRect()?.height;
    const headerHeight = headerRef?.current?.getBoundingClientRect()?.height;
    const tablePagination = 100;
    return windowHeight - headerHeight - tablePagination;
  }, []);

  // Onmount, setup a scroll listening to save the scroll position of the table.
  useEffect(() => {
    const window = document.getElementById(mosaicPanel);
    const tableElement = window?.getElementsByClassName("table_container")[0];
    const setTableScrollTop = (): void => {
      setScrollTop(tableElement?.scrollTop ?? 0);
    };
    if (tableElement) tableElement.addEventListener("scroll", setTableScrollTop);

    // Also resize the table after a slight delay.
    setTimeout(() => setTableHeight(getTableHeight(mosaicPanel)), 100);
    // Remove scroll listener
    return () => {
      if (tableElement) tableElement.removeEventListener("scroll", setTableScrollTop);
    };
  }, [getTableHeight, mosaicPanel]);

  useLayoutEffect(() => {
    // Resize the table if the user resizes the browser or mosaic windows.
    setTableHeight(getTableHeight(mosaicPanel));
  }, [path, getTableHeight, mosaicPanel]);
  // DYNAMIC TABLE RESIZE CODE END

  return (
    <>
      <OCFlightPairingStatus>
        <FlightPairingStatusContextMenu id={id} path={path} setActiveEventModal={setActiveEventModal} />

        <Grid container direction={layout} wrap="nowrap" alignItems="flex-start">
          <SpinnerProgress className={"loadingSpinner"} loading={isLoading} message="Loading Flight Pairing Status" />

          <Grid
            item
            ref={headerRef}
            className={"flightPairingStatusInfo"}
            style={{
              width: "100%", //width: layout === "row" ? "auto" : "100%",
              maxWidth: "100%", //maxWidth: layout === "row" ? "40%" : "100%",
            }}
          >
            <Grid container className="window_title">
              {pageHeader}
              <Button
                className="refresh_btn"
                id={"refresh_flightpairingdata_btn"}
                onClick={() => getFlightPairingStatus()}
                color="primary"
                disabled={isLoading}
                variant="contained"
              >
                {flightStatusMap ? "Refresh" : "Load Status"}
              </Button>
            </Grid>

            <Grid container className="filter_container">
              <Typography className="flight_filter_title" variant="body2" style={{ width: 41 }}>
                Filters
              </Typography>
              <Grid container className="flight_filter">
                <Grid item style={{ display: "flex", flexDirection: "row", flexWrap: "wrap" }}>
                  <DateTimeField
                    className="start range_datetimefield"
                    name="pairing_status_start"
                    label="Start"
                    value={start?.valueOf()}
                    // Always sets the start of this selected date (utc time)
                    onChange={(date) => setStart(moment.utc(date).startOf("day")?.valueOf())}
                    DateProps={{ maxDate: new Date(end - 86400000) }} // end - day.
                    isEpoch={true}
                    hideTimeControl={true}
                    maxDate={end?.valueOf()}
                  />
                  <DateTimeField
                    className="end range_datetimefield"
                    name="pairing_status_end"
                    label="End"
                    value={end?.valueOf()}
                    // Always sets the end of this selected date (utc time)
                    onChange={(date) => setEnd(moment.utc(date).endOf("day")?.valueOf())}
                    DateProps={{ minDate: new Date(start + 86400000) }} // start + day.
                    isEpoch={true}
                    hideTimeControl={true}
                    minDate={start?.valueOf()}
                  />
                </Grid>
                <Grid item>
                  <TextField
                    className="shortcode_textfield"
                    name="pairing_status_shortcode"
                    label="Short Code"
                    value={shortCode}
                    fullWidth={false}
                    onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
                      const code = event?.target?.value;
                      setShortCode(code?.toUpperCase()?.replace(/\s+/g, "") ?? null);
                    }}
                  />
                </Grid>
                <Grid item>
                  <TextField
                    className="flightnumber_textfield"
                    name="pairing_status_flightnumber"
                    label="Flight #"
                    value={flightNumber}
                    fullWidth={false}
                    onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => setFlightNumber(event?.target?.value)}
                  />
                </Grid>
                <Grid item>
                  <SelectField
                    className="origin_selectfield"
                    name="pairing_status_origin"
                    label="Origin"
                    value={origin}
                    onChange={setOrigin}
                    onInputChange={(ident: string) => {
                      if (ident && ident.length === 4) updateAirportOptions(ident, airportOptions, dispatch);
                    }}
                    options={Object.values(airportOptions)}
                    getOptionLabel={(option: IAirportOption) => `${option.ident} - ${option.name}`}
                    getOptionValue={(option: IAirportOption) => option.ident}
                    noOptionsMessage={() => "Search for airport name or IATA code"}
                    isSearchable={true}
                    isClearable={true}
                    isMulti={true}
                  />
                </Grid>
                <Grid item>
                  <SelectField
                    className="destination_selectfield"
                    name="pairing_status_destination"
                    label="Dest."
                    value={destination}
                    onChange={setDestination}
                    onInputChange={(ident: string) => {
                      if (ident && ident.length === 4) updateAirportOptions(ident, airportOptions, dispatch);
                    }}
                    options={Object.values(airportOptions)}
                    getOptionLabel={(option: IAirportOption) => `${option.ident} - ${option.name}`}
                    getOptionValue={(option: IAirportOption) => option.ident}
                    noOptionsMessage={() => "Search for airport name or IATA code"}
                    isSearchable={true}
                    isClearable={true}
                    isMulti={true}
                  />
                </Grid>
                <Grid item>
                  <SelectField
                    className="aircraftgroup_selectfield"
                    name="pairing_status_aircraftgroup"
                    label="AC Group"
                    getOptionLabel={(option: IAircraftGroup) =>
                      `${option.title} (count:${option?.members?.length ?? "0"})`
                    }
                    getOptionValue={(option: IAircraftGroup) => option._id}
                    value={aircraftGroup}
                    onChange={setAircraftGroup}
                    options={aircraftGroupOptions}
                    isSearchable={true}
                    isClearable={true}
                    isMulti={true}
                  />
                </Grid>
                <Grid item>
                  <SelectField
                    className="flighttype_selectfield"
                    name="pairing_status_flighttype"
                    label="Flight Type"
                    value={flightTypes}
                    onChange={(val) => setFlightTypes(val)}
                    options={Object.values(flightTypeList)}
                    getOptionValue={(flightType: IFlightType) => flightType.description}
                    getOptionLabel={(flightType: IFlightType) =>
                      flightType ? `${flightType.title} - ${flightType.description}` : ""
                    }
                    isSearchable={true}
                    isClearable={true}
                    isMulti={true}
                  />
                </Grid>
                <Grid item>
                  <SelectField
                    className="position_selectfield"
                    name="pairing_status_position"
                    label="Positions"
                    value={positions}
                    options={positionOptions}
                    onChange={setPositions}
                    getOptionLabel={(option: IPosition) => option.title}
                    getOptionValue={(option: IPosition) => option._id}
                    isMulti={true}
                    isClearable={true}
                    isSearchable={true}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid container className="legend_container">
              <Typography className="flight_status_legend_title" variant="body2">
                Status
              </Typography>
              <Grid className="legend">
                {Object.values(flightStatusMessages).map((item, index) => (
                  <div key={index} className={`status ${item.className}`}>
                    {item.status}
                  </div>
                ))}
              </Grid>
            </Grid>
            <Grid container className="legend_container">
              <Typography className="flight_status_legend_title" variant="body2">
                Sub Aircraft
              </Typography>
              <Grid className="legend">
                <SwitchField
                  className="subaircraft_switch"
                  name="pairing_status_subaircraftswitch"
                  value={includeSubAircraft}
                  onChange={() => setIncludeSubAircraft(!includeSubAircraft)}
                  switchlabels={["Show", "Hide"]}
                  switchtooltips={["Show Sub Aircraft", "Hide Sub Aircraft"]}
                  label={includeSubAircraft ? "" : ""}
                  allowNull={false}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid container direction="column">
            <FilterSearchTable
              className={"flightPairingStatusTable"}
              id={id}
              path={path}
              events={tableData}
              parentId={mosaicPanel}
              uniqueKey={"id"}
              hideSelectionCheckboxes={true}
              hideFocusSelected={true}
              defaultRowsPerPage={250}
              hideShowColumns={true}
              stickyHeader={true}
              maxTableHeight={tableHeight}
            />
          </Grid>
        </Grid>
      </OCFlightPairingStatus>
      {activeEventModal !== EventModalTypes.None && (
        <DialogModalWrapper modalType={activeEventModal} setActiveEventModal={setActiveEventModal} />
      )}
    </>
  );
};
